import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  FolderPlus,
  Clock,
  Settings,
  Globe,
  AlertCircle,
  CheckCircle2,
  Undo2,
  Redo2,
  Users,
} from 'lucide-react';
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { TodoItemResponse, TodoSectionResponse } from '@/lib/api';
import { useAuth } from '@/auth/hooks/useAuth';
import {
  useTodoList,
  useTodoSections,
  useTodoItems,
  useTodoUndoRedo,
  useTodoShortcuts,
  useTodoCollaborators,
} from '../hooks';
import { useListDetailHandlers } from '../hooks/useListDetailHandlers.js';
import {
  SectionCard,
  TodoItemRow,
  CreateSectionDialog,
  CreateTimeframeDialog,
  CreateItemDialog,
  EditSectionDialog,
  EditItemDialog,
  ListSettingsDialog,
  ShareListDialog,
} from '../components';
import { TodoListProvider } from '../context';
import type { TodoListContextValue } from '../context';

export function ListDetailPage() {
  const { listId } = useParams<{ listId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { list, loading, error } = useTodoList(listId!);
  const { createSection, updateSection, deleteSection } = useTodoSections(listId!);
  const { createItem, updateItem, deleteItem } = useTodoItems(listId!);

  const isOwner = list ? list.ownerId === user?.userId : false;
  const userRole = list?.userRole ?? (isOwner ? 'owner' : null);

  const {
    addCollaborator,
    removeCollaborator,
    assignItem,
    unassignItem,
    assignSection,
    unassignSection,
  } = useTodoCollaborators(listId!);

  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [myItemsOnly, setMyItemsOnly] = useState(false);
  const [filterByCollaboratorId, setFilterByCollaboratorId] = useState<string | null>(null);

  const handleAssignItem = useCallback(
    (itemId: string, username: string) => assignItem(itemId, { username }),
    [assignItem],
  );
  const handleUnassignItem = useCallback(
    (itemId: string, userId: string) => unassignItem(itemId, userId),
    [unassignItem],
  );
  const handleAssignSection = useCallback(
    (sectionId: string, username: string) => assignSection(sectionId, { username }),
    [assignSection],
  );
  const handleUnassignSection = useCallback(
    (sectionId: string, userId: string) => unassignSection(sectionId, userId),
    [unassignSection],
  );

  // Helper: check if a given user is assigned to a section or any ancestor section
  const isSectionAssignedToUser = useCallback(
    (sectionId: string | null, userId: string): boolean => {
      if (!list || !sectionId) return false;
      let currentId: string | null = sectionId;
      while (currentId) {
        if (
          list.sectionAssignments?.some((a) => a.sectionId === currentId && a.userId === userId)
        ) {
          return true;
        }
        const section = list.sections.find((s) => s.sectionId === currentId);
        currentId = section?.parentSectionId ?? null;
      }
      return false;
    },
    [list],
  );

  const isItemAssignedToUser = useCallback(
    (itemId: string, sectionId: string | null, userId: string) => {
      if (!list) return false;
      if (list.itemAssignments?.some((a) => a.itemId === itemId && a.userId === userId))
        return true;
      return isSectionAssignedToUser(sectionId, userId);
    },
    [list, isSectionAssignedToUser],
  );

  const isItemAssignedToMe = useCallback(
    (itemId: string, sectionId: string | null) => {
      if (!user?.userId) return false;
      return isItemAssignedToUser(itemId, sectionId, user.userId);
    },
    [user?.userId, isItemAssignedToUser],
  );

  const hasEditAccess = isOwner || userRole === 'editor';
  const {
    canUndo,
    canRedo,
    undo,
    redo,
    captureSnapshot,
    clear: clearUndoHistory,
  } = useTodoUndoRedo({ listId: listId!, enabled: hasEditAccess });

  useTodoShortcuts({ onUndo: undo, onRedo: redo });

  // Clear undo history when sharing permissions change
  const prevAssignmentsRef = useRef<string>('');
  useEffect(() => {
    const key = JSON.stringify({
      c: list?.collaborators?.map((c) => `${c.userId}:${c.role}`).sort(),
      i: list?.itemAssignments?.map((a) => `${a.itemId}:${a.userId}`).sort(),
      s: list?.sectionAssignments?.map((a) => `${a.sectionId}:${a.userId}`).sort(),
    });
    if (prevAssignmentsRef.current && prevAssignmentsRef.current !== key) {
      clearUndoHistory();
    }
    prevAssignmentsRef.current = key;
  }, [list?.collaborators, list?.itemAssignments, list?.sectionAssignments, clearUndoHistory]);

  // Dialog state
  const [createSectionOpen, setCreateSectionOpen] = useState(false);
  const [createTimeframeOpen, setCreateTimeframeOpen] = useState(false);
  const [createItemOpen, setCreateItemOpen] = useState(false);
  const [editSectionOpen, setEditSectionOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<TodoSectionResponse | null>(null);
  const [editItemOpen, setEditItemOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<TodoItemResponse | null>(null);
  const [listSettingsOpen, setListSettingsOpen] = useState(false);

  // Extracted handlers
  const handlers = useListDetailHandlers(
    {
      listId: listId!,
      list,
      createSection,
      updateSection,
      deleteSection,
      createItem,
      updateItem,
      deleteItem,
      captureSnapshot,
      undo,
    },
    {
      setCreateSectionOpen,
      setCreateTimeframeOpen,
      setCreateItemOpen,
      setEditSectionOpen,
      setEditingSection,
      setEditItemOpen,
      setEditingItem,
    },
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  // Derived data
  const activeItems = useMemo(() => {
    if (!list) return [];
    const items = list.items.filter((i) => i.status !== 'completed');
    if (isOwner && filterByCollaboratorId) {
      return items.filter((item) =>
        isItemAssignedToUser(item.itemId, item.sectionId, filterByCollaboratorId),
      );
    }
    if (!isOwner && myItemsOnly && user?.userId) {
      return items.filter((item) => isItemAssignedToMe(item.itemId, item.sectionId));
    }
    return items;
  }, [
    list,
    isOwner,
    filterByCollaboratorId,
    myItemsOnly,
    user?.userId,
    isItemAssignedToUser,
    isItemAssignedToMe,
  ]);

  const sectionsWithItems = useMemo(() => {
    if (!list) return new Set<string>();
    const ids = new Set<string>();
    for (const item of activeItems) {
      if (item.sectionId) {
        let currentId: string | null = item.sectionId;
        while (currentId) {
          if (ids.has(currentId)) break;
          ids.add(currentId);
          const section = list.sections.find((s) => s.sectionId === currentId);
          currentId = section?.parentSectionId ?? null;
        }
      }
    }
    return ids;
  }, [list, activeItems]);

  const isFiltering = (isOwner && !!filterByCollaboratorId) || (!isOwner && myItemsOnly);

  const topLevelSections = useMemo(() => {
    if (!list) return [];
    return list.sections
      .filter((s) => s.parentSectionId === null)
      .filter((s) => !isFiltering || sectionsWithItems.has(s.sectionId))
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [list, isFiltering, sectionsWithItems]);

  const unsortedItems = useMemo(() => {
    return activeItems.filter((item) => item.sectionId === null);
  }, [activeItems]);

  const topLevelSectionIds = useMemo(
    () => topLevelSections.map((s) => s.sectionId),
    [topLevelSections],
  );

  const handleTopLevelDragEnd = useMemo(
    () => handlers.createTopLevelDragEnd(topLevelSections),
    [handlers, topLevelSections],
  );
  const handleUnsortedItemDragEnd = useMemo(
    () => handlers.createUnsortedItemDragEnd(unsortedItems),
    [handlers, unsortedItems],
  );

  // Context value
  const contextValue: TodoListContextValue = useMemo(
    () => ({
      isOwner,
      collaborators: list?.collaborators ?? [],
      itemAssignments: list?.itemAssignments ?? [],
      sectionAssignments: list?.sectionAssignments ?? [],
      currentUserId: user?.userId,
      visibleSectionIds: isFiltering ? sectionsWithItems : undefined,
      onToggleItem: handlers.handleToggleItem,
      onEditItem: handlers.handleEditItem,
      onDeleteItem: handlers.handleDeleteItem,
      onEditSection: handlers.handleEditSection,
      onDeleteSection: handlers.handleDeleteSection,
      onCreateItem: handlers.handleCreateItem,
      onCreateSection: createSection,
      onReorderItems: handlers.optimisticItemReorder,
      onReorderSections: handlers.optimisticSectionReorder,
      onUpdateItemTitle: handlers.handleUpdateItemTitle,
      onCreateItemBelow: handlers.handleCreateItemBelow,
      onAssignItem: handleAssignItem,
      onUnassignItem: handleUnassignItem,
      onAssignSection: handleAssignSection,
      onUnassignSection: handleUnassignSection,
      onToggleHiddenFromPublic: handlers.handleToggleHiddenFromPublic,
    }),
    [
      isOwner,
      list?.collaborators,
      list?.itemAssignments,
      list?.sectionAssignments,
      user?.userId,
      isFiltering,
      sectionsWithItems,
      handlers,
      createSection,
      handleAssignItem,
      handleUnassignItem,
      handleAssignSection,
      handleUnassignSection,
    ],
  );

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-96" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !list) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{error || 'List not found'}</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate('/a/arche_todo_lists')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to lists
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <TodoListProvider value={contextValue}>
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/a/arche_todo_lists')}
              aria-label="Back to lists"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-2xl font-semibold flex-1 truncate">{list.name}</h1>
            {hasEditAccess && (
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={undo}
                  disabled={!canUndo}
                  aria-label="Undo"
                  title="Undo (Ctrl+Z)"
                >
                  <Undo2 className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={redo}
                  disabled={!canRedo}
                  aria-label="Redo"
                  title="Redo (Ctrl+Shift+Z)"
                >
                  <Redo2 className="h-4 w-4" />
                </Button>
              </div>
            )}
            <Link to={`/a/arche_todo_lists/${listId}/completed`}>
              <Button variant="outline" size="sm">
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Completed
              </Button>
            </Link>
            {isOwner && (
              <Button variant="outline" size="sm" onClick={() => setShareDialogOpen(true)}>
                <Users className="h-4 w-4 mr-2" />
                Share
              </Button>
            )}
            {isOwner && (
              <Button variant="outline" size="sm" onClick={() => setListSettingsOpen(true)}>
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </Button>
            )}
          </div>
          {list.description && (
            <p className="text-sm text-muted-foreground ml-11">{list.description}</p>
          )}
          {list.isPublic && list.publicSlug && (
            <div className="flex items-center gap-2 ml-11 text-sm text-muted-foreground">
              <Globe className="h-3.5 w-3.5" />
              <span>Public link:</span>
              <Link
                to={`/a/arche_todo_lists/public/${list.publicSlug}`}
                className="text-primary hover:underline"
              >
                /a/arche_todo_lists/public/{list.publicSlug}
              </Link>
            </div>
          )}
        </div>

        {/* Collaborator filter for owner */}
        {isOwner && list.collaborators && list.collaborators.length > 0 && (
          <div className="flex items-center gap-2">
            <Select
              value={filterByCollaboratorId ?? 'all'}
              onValueChange={(v) => setFilterByCollaboratorId(v === 'all' ? null : v)}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All collaborators" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All collaborators</SelectItem>
                {list.collaborators.map((c) => (
                  <SelectItem key={c.userId} value={c.userId}>
                    {c.username}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* My Items toggle for non-owner collaborators */}
        {!isOwner && userRole && (
          <div className="flex items-center gap-2">
            <Button
              variant={myItemsOnly ? 'default' : 'outline'}
              size="sm"
              onClick={() => setMyItemsOnly(!myItemsOnly)}
            >
              {myItemsOnly ? 'Show All' : 'My Items Only'}
            </Button>
          </div>
        )}

        {/* Action bar */}
        {isOwner && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setCreateTimeframeOpen(true)}>
              <Clock className="h-4 w-4 mr-2" />
              Add Timeframe
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCreateSectionOpen(true)}>
              <FolderPlus className="h-4 w-4 mr-2" />
              Add Section
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCreateItemOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Item
            </Button>
          </div>
        )}

        {/* Sections */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleTopLevelDragEnd}
        >
          <div className="space-y-4">
            <SortableContext items={topLevelSectionIds} strategy={verticalListSortingStrategy}>
              {topLevelSections.map((section) => (
                <SectionCard
                  key={section.sectionId}
                  section={section}
                  sections={list.sections}
                  items={activeItems}
                />
              ))}
            </SortableContext>

            {unsortedItems.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Unsorted
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1">
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleUnsortedItemDragEnd}
                  >
                    <SortableContext
                      items={unsortedItems.map((i) => i.itemId)}
                      strategy={verticalListSortingStrategy}
                    >
                      {unsortedItems.map((item) => (
                        <TodoItemRow
                          key={item.itemId}
                          item={item}
                          canEdit={isOwner || isItemAssignedToMe(item.itemId, null)}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                </CardContent>
              </Card>
            )}

            {topLevelSections.length === 0 && unsortedItems.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <p>This list is empty. Add sections or items to get started.</p>
              </div>
            )}
          </div>
        </DndContext>

        {/* Dialogs */}
        <CreateTimeframeDialog
          open={createTimeframeOpen}
          onClose={() => setCreateTimeframeOpen(false)}
          onCreate={handlers.handleCreateTimeframe}
        />
        <CreateSectionDialog
          open={createSectionOpen}
          onClose={() => setCreateSectionOpen(false)}
          onCreate={handlers.handleCreateSection}
          sections={list.sections}
        />
        <EditSectionDialog
          open={editSectionOpen}
          onClose={() => {
            setEditSectionOpen(false);
            setEditingSection(null);
          }}
          section={editingSection}
          sections={list.sections}
          onSave={handlers.handleSaveSection}
        />
        <EditItemDialog
          open={editItemOpen}
          onClose={() => {
            setEditItemOpen(false);
            setEditingItem(null);
          }}
          item={editingItem}
          sections={list.sections}
          onSave={handlers.handleSaveItem}
        />
        <CreateItemDialog
          open={createItemOpen}
          onClose={() => setCreateItemOpen(false)}
          onCreate={handlers.handleCreateItem}
          sections={list.sections}
        />
        <ShareListDialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          collaborators={list.collaborators ?? []}
          onAdd={addCollaborator}
          onRemove={removeCollaborator}
        />
        {isOwner && (
          <ListSettingsDialog
            open={listSettingsOpen}
            onClose={() => setListSettingsOpen(false)}
            list={list}
            onSave={handlers.handleSettingsSave}
          />
        )}
      </div>
    </TodoListProvider>
  );
}
